import { Event, EventTicket } from "@features/events/types";

interface SlugAvailabilityResponse {
  slug: string;
  exists: boolean;
  id?: string;
}

interface DuplicateEventResult {
  success: boolean;
  newEventId?: string;
  error?: string;
}

type GetRequest = <T>(
  url: string,
  showToast?: boolean,
  showLoading?: boolean,
  disableErrorHandling?: boolean
) => Promise<T>;

type PostRequest = <T>(url: string, data: object) => Promise<T>;

type EventPayload = Omit<Partial<Event>, "timezone"> & { timezone?: string };

/**
 * Generate a unique slug by appending -2, -3, etc. to the base slug
 * If the slug already ends with a number (e.g., slug-2), it will increment that number
 * @param baseSlug - The original event slug
 * @param hcc - Healthcare company ID
 * @param GET - API request function
 * @returns Promise<string> - A unique slug
 */
export async function generateUniqueSlug(
  baseSlug: string,
  hcc: string,
  GET: GetRequest
): Promise<string> {
  // Check if the slug already ends with -<number>
  const match = baseSlug.match(/^(.*)-(\d+)$/);
  
  let slugBase: string;
  let counter: number;
  
  if (match) {
    // If slug already has a number suffix, extract the base and increment from that number
    slugBase = match[1];
    counter = parseInt(match[2], 10) + 1;
  } else {
    // If no number suffix, use the full slug as base and start from 2
    slugBase = baseSlug;
    counter = 2;
  }

  let candidateSlug = `${slugBase}-${counter}`;
  let isAvailable = false;

  // Keep trying until we find an available slug
  while (!isAvailable) {
    try {
      const response = await GET<SlugAvailabilityResponse>(
        `/api/healthcare-companies/${hcc}/e/events/slug-availability/${encodeURIComponent(candidateSlug)}`,
        false,
        false,
        true
      );

      // If slug doesn't exist, it's available
      if (!response.exists) {
        isAvailable = true;
        return candidateSlug;
      }

      // If it exists, try the next number
      counter++;
      candidateSlug = `${slugBase}-${counter}`;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      // If there's an error checking availability, throw it
      throw new Error(`Failed to check slug availability: ${errorMessage}`);
    }
  }

  return candidateSlug;
}

/**
 * Duplicate an event with optional copying of tickets, agenda, and gallery
 * @param originalEvent - The event to duplicate
 * @param hcc - Healthcare company ID
 * @param options - Copy options for tickets, agenda, and gallery
 * @param POST - API POST request function
 * @param GET - API GET request function
 * @returns Promise<DuplicateEventResult>
 */
export async function duplicateEvent(
  originalEvent: Event,
  hcc: string,
  options: {
    copyTickets: boolean;
    copyAgenda: boolean;
    copyGallery: boolean;
  },
  POST: PostRequest,
  GET: GetRequest
): Promise<DuplicateEventResult> {
  try {
    // Step 1: Generate unique slug
    const newSlug = await generateUniqueSlug(originalEvent.slug, hcc, GET);

    // Step 2: Prepare event data for duplication
    const eventDataToDuplicate: EventPayload = {
      // Copy all fields from original event
      title: originalEvent.title,
      description: originalEvent.description,
      formatId: originalEvent.formatId,
      dateStart: originalEvent.dateStart,
      dateEnd: originalEvent.dateEnd,
      languageCodes: originalEvent.languageCodes,
      book: originalEvent.book,
      sourceUrl: originalEvent.sourceUrl?.trim()
        ? originalEvent.sourceUrl.trim()
        : null,
      bookRedirectLink: originalEvent.bookRedirectLink,
      spotsMax: originalEvent.spotsMax,
      priceCurrency: originalEvent.priceCurrency,
      categoryIds: originalEvent.categoryIds,
      organizerIds: originalEvent.organizerIds,
      instructorIds: originalEvent.instructorIds,
      brandIds: originalEvent.brandIds,
      venueIds: originalEvent.venueIds,
      // Transform timezone object to just the ID for the API
      timezone: originalEvent.timezone?.id,
      expertiseLevelIds: originalEvent.expertiseLevelIds,
      // Only include gallery if user chose to copy it and it has content
      gallery: options.copyGallery && 
               originalEvent.gallery && 
               originalEvent.gallery.media && 
               originalEvent.gallery.media.length > 0
        ? originalEvent.gallery
        : undefined,
      whyAttend: originalEvent.whyAttend,
      faq: originalEvent.faq,
      testimonials: originalEvent.testimonials,
      // Only include agenda if user chose to copy it and it has content
      agenda: options.copyAgenda && 
              originalEvent.agenda && 
              originalEvent.agenda.length > 0
        ? originalEvent.agenda
        : undefined,
      relatedEvents: originalEvent.relatedEvents,
      enableOnlinePayments: originalEvent.enableOnlinePayments,

      // Set new slug
      slug: newSlug,

      // Reset fields for new event
      spotsCurrent: 0,
      featured: false,
      verified: false,
      visibility: 0,
      // Reset private notes to undefined
      privateNote: undefined,
      privateNoteTone: undefined,
    };

    // Remove id field (will be auto-generated)
    // Remove tickets array (will be created separately if needed)
    // These are implicitly not included in eventDataToDuplicate

    // Step 3: Create the new event
    const newEvent = await POST<Event>(
      `/api/healthcare-companies/${hcc}/e/events`,
      eventDataToDuplicate
    );

    if (!newEvent || !newEvent.id) {
      throw new Error("Failed to create duplicated event - no ID returned");
    }

    // Step 4: Copy tickets if requested
    if (options.copyTickets && originalEvent.tickets && originalEvent.tickets.length > 0) {
      // Create tickets sequentially to maintain order
      for (const originalTicket of originalEvent.tickets) {
        try {
          // Prepare ticket data without id
          const ticketDataToCopy: Omit<EventTicket, "id"> = {
            title: originalTicket.title,
            description: originalTicket.description,
            price: originalTicket.price,
            minQuantity: originalTicket.minQuantity,
            currency: originalTicket.currency,
            visibility: originalTicket.visibility,
            index: originalTicket.index,
            status: originalTicket.status,
            expiresAt: originalTicket.expiresAt,
            startsAt: originalTicket.startsAt,
          };

          // Create the ticket for the new event
          await POST(
            `/api/healthcare-companies/${hcc}/e/events/${newEvent.id}/tickets`,
            ticketDataToCopy
          );
        } catch (ticketError) {
          console.error("Error copying ticket:", ticketError);
          // Continue with other tickets even if one fails
        }
      }
    }

    // Step 5: Return success with new event ID
    return {
      success: true,
      newEventId: newEvent.id,
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error("Error duplicating event:", error);
    return {
      success: false,
      error: errorMessage || "Failed to duplicate event",
    };
  }
}
